<template>
  <div class="space-y-1">
    <VcLabel v-if="label" :required="required">{{ label }}</VcLabel>

    <div class="rounded-md border border-neutral-200 p-3">
      <!-- File preview when uploaded -->
      <div v-if="hasAsset" class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <!-- File type icon -->
          <div class="flex size-10 items-center justify-center rounded-lg bg-neutral-100">
            <VcIcon v-if="isImage" name="photograph" class="text-neutral-500" />

            <VcIcon v-else name="document" class="text-neutral-500" />
          </div>

          <div class="flex flex-col">
            <span class="text-sm font-medium text-neutral-900">{{ fileName }}</span>

            <span class="text-xs text-neutral-500">{{ fileSize }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Preview button -->
          <VcButton
            v-if="previewUrl"
            variant="outline"
            color="secondary"
            size="sm"
            icon="eye"
            :disabled="disabled"
            @click="handlePreview"
          >
            {{ $t("arad.address_extension.file.preview") }}
          </VcButton>

          <!-- Delete button -->
          <VcButton
            variant="outline"
            color="danger"
            size="sm"
            icon="trash"
            :disabled="disabled || loading"
            :loading="loading"
            @click="handleDelete"
          >
            {{ $t("arad.address_extension.file.delete") }}
          </VcButton>
        </div>
      </div>

      <!-- Upload area when no file -->
      <div v-else>
        <!-- Hidden file input -->
        <input
          :id="inputId"
          ref="fileInput"
          type="file"
          :accept="allowedTypes.join(',')"
          :disabled="disabled"
          class="sr-only"
          @change="handleFileSelect"
        />

        <label
          :for="inputId"
          :class="[
            'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
            disabled ? 'opacity-50' : 'border-neutral-300 hover:border-primary-500 hover:bg-primary-50',
            uploadError ? 'border-danger-500 bg-danger-50' : '',
          ]"
          role="button"
          tabindex="0"
          @keydown.enter="triggerFileInput"
          @keydown.space="triggerFileInput"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <VcIcon name="cloud-upload" size="lg" class="mb-2 text-neutral-400" />

          <p class="text-sm text-neutral-600">
            {{ $t("arad.address_extension.file.drag_drop") }}
          </p>

          <p class="mt-1 text-xs text-neutral-500">
            {{ $t("arad.address_extension.file.allowed_types", { types: allowedExtensions }) }}
          </p>

          <p class="text-xs text-neutral-500">
            {{ $t("arad.address_extension.file.max_size", { size: maxFileSizeFormatted }) }}
          </p>

          <span v-if="!uploading" class="mt-3 text-sm font-medium text-primary-600">
            {{ $t("arad.address_extension.file.select") }}
          </span>

          <div v-if="uploading" class="mt-3 flex items-center gap-2">
            <VcLoader size="sm" />

            <span class="text-sm text-neutral-600">{{ $t("arad.address_extension.file.uploading") }}</span>
          </div>
        </label>

        <p v-if="uploadError" class="mt-2 text-sm text-danger-500">
          {{ translatedError }}
        </p>
      </div>
    </div>

    <p v-if="error && !uploadError" class="text-sm text-danger-500">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getPrivateAsset, uploadPrivateAsset, deletePrivateAsset } from "../api";
import { FILE_VALIDATION_CONFIG, formatFileSize, validateFile } from "../utils/validation";
import type { PrivateAssetType, IPrivateAssetResponse } from "../types";

interface IProps {
  /** Asset ID (v-model) */
  assetId?: string | null;
  /** Type of asset */
  assetType: PrivateAssetType;
  /** Label text */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Error message from parent */
  error?: string;
}

interface IEmits {
  (event: "update:assetId", value: string | null): void;
}

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const { t } = useI18n();

const fileInput = ref<HTMLInputElement | null>(null);

// Generate stable unique ID
let idCounter = 0;
const inputId = `file-upload-${++idCounter}`;

// Local state for asset management (NOT using composable to avoid memory leaks)
const asset = ref<IPrivateAssetResponse | null>(null);
const loading = ref(false);
const uploading = ref(false);
const uploadError = ref<string | null>(null);

// Computed properties derived from local state
const hasAsset = computed(() => asset.value !== null && !!asset.value.id);
const fileName = computed(() => asset.value?.fileName ?? "");
const fileSize = computed(() => {
  if (!asset.value?.fileSize) return "";
  return formatFileSize(asset.value.fileSize);
});
const previewUrl = computed(() => asset.value?.signedUrl ?? null);
const allowedTypes = FILE_VALIDATION_CONFIG.allowedMimeTypes;
const maxFileSize = FILE_VALIDATION_CONFIG.maxSize;

// Computed properties for display
const allowedExtensions = computed(() => FILE_VALIDATION_CONFIG.allowedExtensions.join(", "));
const maxFileSizeFormatted = computed(() => formatFileSize(maxFileSize));

const isImage = computed(() => {
  if (!fileName.value) return false;
  const ext = fileName.value.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png"].includes(ext ?? "");
});

const translatedError = computed(() => {
  if (!uploadError.value) return "";
  const key = `arad.address_extension.file.errors.${uploadError.value}`;
  const translated = t(key);
  return translated === key ? uploadError.value : translated;
});

// Watch for assetId prop changes - load asset when ID changes
// Using a flag to prevent duplicate API calls
let lastLoadedId: string | null | undefined = undefined;

watch(
  () => props.assetId,
  async (newId) => {
    // Skip if same ID (prevents duplicate loads)
    if (newId === lastLoadedId) {
      return;
    }
    lastLoadedId = newId;

    if (newId) {
      loading.value = true;
      try {
        asset.value = await getPrivateAsset(newId);
      } catch {
        asset.value = null;
      } finally {
        loading.value = false;
      }
    } else {
      asset.value = null;
    }
  },
  { immediate: true },
);

// Methods
function triggerFileInput(): void {
  fileInput.value?.click();
}

async function handleFileSelect(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (file) {
    await uploadFile(file);
  }

  input.value = "";
}

async function uploadFile(file: File): Promise<void> {
  // Validate file before upload
  const validation = validateFile(file);
  if (!validation.valid) {
    uploadError.value = validation.error ?? "Invalid file";
    return;
  }

  uploading.value = true;
  uploadError.value = null;

  try {
    const result = await uploadPrivateAsset(file, props.assetType);

    if (!result.success) {
      uploadError.value = result.error ?? "Upload failed";
      return;
    }

    asset.value = result.asset as IPrivateAssetResponse;
    lastLoadedId = result.asset.id; // Update lastLoadedId to prevent re-fetch
    emit("update:assetId", result.asset.id);
  } catch (err) {
    uploadError.value = err instanceof Error ? err.message : "Upload failed";
  } finally {
    uploading.value = false;
  }
}

async function handleDelete(): Promise<void> {
  if (!asset.value?.id) {
    emit("update:assetId", null);
    return;
  }

  loading.value = true;
  uploadError.value = null;

  try {
    await deletePrivateAsset(asset.value.id);
    asset.value = null;
    lastLoadedId = null; // Reset lastLoadedId
    emit("update:assetId", null);
  } catch (err) {
    // Even if delete fails on server, clear locally to allow re-upload
    uploadError.value = err instanceof Error ? err.message : "Delete failed";
    asset.value = null;
    lastLoadedId = null;
    emit("update:assetId", null);
  } finally {
    loading.value = false;
  }
}

function handlePreview(): void {
  if (previewUrl.value) {
    window.open(previewUrl.value, "_blank");
  }
}

function handleDragOver(event: DragEvent): void {
  if (!props.disabled) {
    (event.currentTarget as HTMLElement).classList.add("border-primary-500", "bg-primary-50");
  }
}

function handleDragLeave(event: DragEvent): void {
  (event.currentTarget as HTMLElement).classList.remove("border-primary-500", "bg-primary-50");
}

async function handleDrop(event: DragEvent): Promise<void> {
  (event.currentTarget as HTMLElement).classList.remove("border-primary-500", "bg-primary-50");

  if (props.disabled) return;

  const file = event.dataTransfer?.files?.[0];
  if (file) {
    await uploadFile(file);
  }
}
</script>
